import { isAccountingJournalTarget, tryCallApi } from 'common/helpers'
import { AssetLedger, FORM_MODES, LedgerSetting } from 'common/models'
import { FixedAssetsService, PresenterRevisedAcquisitionCostResponse } from 'common/open-api'
import { getAxiosSilent } from 'common/open-api/helper'
import { FeatureFlagNames } from 'common/open-features'
import { useEnableFlagRef } from 'common/open-features/hooks'
import { getIn, useFormikContext } from 'formik'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectLedgerSettingList } from 'store/settings/ledgerSetting/selectors'

import { FORM_FIELDS, LEDGERS_FIELD_NAME } from '../const'
import { getAllLedgerFieldNames } from '../helper'
import { filterLedgers, isLedgerForCalculateRevise } from '../helpers/calculate-revise-acquisition'
import { useWaitFormReady } from './use-wait-form-ready'

export const useCalculateRevisedAcquisitionCost = () => {
  const { values, setFieldValue } = useFormikContext()
  const valuesRef = useRef(values)
  valuesRef.current = values

  const ledgerSettings: LedgerSetting[] = useSelector(selectLedgerSettingList, isEqual)

  return useCallback(
    async (
      acquisitionCost?: number,
      subsidyAmount?: number,
      transitionAt?: string,
      usageStartedAt?: string,
      ledgers?: (Partial<AssetLedger> & { ledgerIndex?: number })[]
    ) => {
      const allLedgers = getIn(valuesRef.current, LEDGERS_FIELD_NAME) as AssetLedger[]

      const accountantLedger = allLedgers.find(ledger =>
        isAccountingJournalTarget(ledgerSettings?.find(ledgerSetting => ledgerSetting.id === ledger.ledger_setting_id))
      )

      if ((!acquisitionCost || !usageStartedAt) && transitionAt) {
        ledgers?.filter(isLedgerForCalculateRevise)?.forEach((ledger, index) => {
          const revisedField = getAllLedgerFieldNames(ledger.ledgerIndex ?? index).revisedAcquisitionAmountField
          setFieldValue(revisedField, 0)
        })
      }

      if (
        !acquisitionCost ||
        acquisitionCost < 0 ||
        !transitionAt ||
        !usageStartedAt ||
        !isLedgerForCalculateRevise(accountantLedger)
      ) {
        return
      }

      const isIncludeAccountingLedger = ledgers?.some(ledger =>
        isAccountingJournalTarget(ledgerSettings?.find(ledgerSetting => ledgerSetting.id === ledger.ledger_setting_id))
      )
      // the revise calculation logic on tax ledgers depends on the accountant ledger,
      // so if the accountant ledger is included in the ledgers to be calculated, we need to include all ledgers in the calculation.
      const ledgersToCalculate = isIncludeAccountingLedger ? allLedgers : ledgers
      const { ledgersForReset, ledgersForCalculate } = filterLedgers(
        ledgersToCalculate,
        accountantLedger,
        ledgerSettings
      )

      if (ledgersForReset.length > 0) {
        ;(ledgersToCalculate ?? [])
          .map((ledger, index) => ({
            ...ledger,
            ledgerIndex: (ledger as any).ledgerIndex ?? index,
          }))
          .filter(ledger => ledgersForReset.some(l => l.ledger_setting_id === ledger.ledger_setting_id))
          .forEach(ledger => {
            const revisedField = getAllLedgerFieldNames(ledger.ledgerIndex).revisedAcquisitionAmountField
            setFieldValue(revisedField, 0)
          })
      }

      if (ledgersForCalculate.length > 0) {
        const { data } = await tryCallApi(FixedAssetsService.postFixedAssetsCalculateRevisedAcquisitionCost)(
          {
            acquisition_cost: acquisitionCost ?? 0,
            subsidy_amount: subsidyAmount ?? 0,
            transition_at: transitionAt ?? '',
            usage_started_at: usageStartedAt ?? '',
            ledgers: (ledgersToCalculate ?? [])
              .map((ledger, index) => ({
                ...ledger,
                ledgerIndex: (ledger as any).ledgerIndex ?? index,
              }))
              .filter(ledger => ledgersForCalculate.some(l => l.ledger_setting_id === ledger.ledger_setting_id))
              .map(ledger => ({
                depreciation_method: Number(ledger.depreciation_method_code),
                id: ledger.ledgerIndex + 1,
                ledger_setting_id: Number(ledger.ledger_setting_id),
                service_life: Number(ledger.service_life),
              })),
          },
          getAxiosSilent()
        )
        ;(data?.data as PresenterRevisedAcquisitionCostResponse | undefined)?.ledgers?.forEach(ledger => {
          const revisedField = getAllLedgerFieldNames(ledger.id - 1).revisedAcquisitionAmountField
          setFieldValue(revisedField, ledger.revised_acquisition_amount ?? 0)
        })
      }
    },
    [ledgerSettings]
  )
}

export const useCalculateRevisedAcquisitionCostAllLedgers = (
  disabled: boolean,
  formMode: FORM_MODES,
  acquisitionCost?: number
) => {
  const { values } = useFormikContext()
  const transitionAt = getIn(values, 'transition_at')
  const usageStartedAt = getIn(values, 'usage_started_at')
  const subsidyAmount = getIn(values, 'subsidy_amount')

  const valuesRef = useRef(values)
  valuesRef.current = values

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.RevisedAcquisitionCost, flagEnableRef)

  const calculateRevisedAcquisitionCost = useCalculateRevisedAcquisitionCost()

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const ledgers = getIn(valuesRef.current, LEDGERS_FIELD_NAME) as AssetLedger[]
    calculateRevisedAcquisitionCost(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
  }, [acquisitionCost, transitionAt, usageStartedAt, subsidyAmount, disabled, calculateRevisedAcquisitionCost])
}

export const useCalculateRevisedAcquisitionCostLedger = (
  disabled: boolean,
  formMode: FORM_MODES,
  ledgerIndex: number
) => {
  const { values } = useFormikContext()
  const valuesRef = useRef(values)
  valuesRef.current = values

  const allLedgerFields = getAllLedgerFieldNames(ledgerIndex)
  const depreciationMethodCode = getIn(valuesRef.current, allLedgerFields.depreciationMethodCodeField)
  const ledgerSettingId = getIn(valuesRef.current, allLedgerFields.ledgerSettingIdField)
  const serviceLife = getIn(valuesRef.current, allLedgerFields.serviceLifeField)
  const transitionBookValue = getIn(valuesRef.current, allLedgerFields.transitionBookValueField)

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.RevisedAcquisitionCost, flagEnableRef)

  const calculateRevisedAcquisitionCost = useCalculateRevisedAcquisitionCost()

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const acquisitionCost = getIn(valuesRef.current, FORM_FIELDS.ACQUISITION_COST_REFERENCE)
    const transitionAt = getIn(valuesRef.current, 'transition_at')
    const usageStartedAt = getIn(valuesRef.current, 'usage_started_at')
    const subsidyAmount = getIn(valuesRef.current, 'subsidy_amount')

    const ledgers = [
      {
        ledgerIndex,
        depreciation_method_code: depreciationMethodCode,
        ledger_setting_id: ledgerSettingId,
        service_life: serviceLife,
        transition_book_value: transitionBookValue,
      },
    ]

    calculateRevisedAcquisitionCost(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
  }, [
    depreciationMethodCode,
    ledgerSettingId,
    serviceLife,
    transitionBookValue,
    ledgerIndex,
    disabled,
    calculateRevisedAcquisitionCost,
  ])
}
